import {
  AdvancedSearchURLParams,
  CollectionTypeOptions,
} from '@ltvco/refresh-lib/v2';

import { ConsolidatedFilterFormInfo } from './interfaces';

/**
 * Parses the information from the `ConsolidatedFilterFormInfo` form into `AdvancedSearchURLParams`.
 * This is used to prepare the query parameters for redirection to the `SearchPersonResultsV2` page.
 *
 * @param info - The form data submitted by the user from the ConsolidatedFilterForm form.
 *
 * @returns `urlParams` - The parsed URL parameters ready for use in `redirectToSearchPersonUrlv2`
 *
 */
export function parseAdvancedSearchURLParams(
  info: ConsolidatedFilterFormInfo
): AdvancedSearchURLParams {
  const {
    fName,
    mName,
    lName,
    location,
    title,
    company,
    level,
    industry,
    company_size_max,
    company_size_min,
    birthMonth,
    birthYear,
    deathMonth,
    deathYear,
    country,
    eventType,
    eventMonth,
    eventYear,
    selectedSources,
    advancedSearch,
  } = info;

  const isDeceased = deathYear.length > 0;

  return {
    fullName: [fName, mName, lName].filter(Boolean).join(' '),
    fName,
    mName,
    lName,
    title,
    company,
    level,
    industry,
    company_size_min,
    company_size_max,
    birthMonth,
    birthYear,
    deathMonth,
    deathYear,
    // We omit this information since ConsolidatedFilterForm doesn't have those fields
    address: '',
    city: '',
    state: '',
    age: '',
    isDeceased,
    location,
    country,
    eventType,
    eventMonth,
    eventYear,
    advancedSearch,
    source: Object.keys(selectedSources).filter((key) => selectedSources[key]),
  };
}

const parseFullName = (fullName: string) => {
  const nameParts = fullName.trim().split(' ');
  const fName = nameParts[0] || '';
  const mName = nameParts.length === 3 ? nameParts[1] : '';
  const lName = nameParts.length === 3 ? nameParts[2] : nameParts[1] || '';

  return { fName, mName, lName };
};

/**
 * Converts `AdvancedSearchURLParams` into `ConsolidatedFilterFormInfo`.
 * This function is used to transform the parsed query parameters into the format required
 * to pre-populate the ConsolidatedFilterForm form.
 *
 * @param {AdvancedSearchURLParams} params - The parsed URL parameters from `getSearchQueryParams`.
 *
 * @returns {ConsolidatedFilterInfo} `info` - Information compatible with `ConsolidatedFilterForm` form.
 */
export function parseConsolidatedFilterFormInfo(
  params: AdvancedSearchURLParams
): ConsolidatedFilterFormInfo {
  const {
    fullName,
    fName,
    lName,
    title,
    company,
    level,
    industry,
    company_size_min,
    company_size_max,
    birthMonth,
    birthYear,
    deathMonth,
    deathYear,
    mName,
    age,
    location,
    country,
    eventType,
    eventMonth,
    eventYear,
    advancedSearch,
    source,
  } = params;

  const info: ConsolidatedFilterFormInfo = {
    fName,
    mName,
    lName,
    title,
    company: company || '',
    level,
    industry,
    company_size_max,
    company_size_min,
    birthMonth: birthMonth || '',
    birthYear: birthYear || '',
    deathMonth: deathMonth || '',
    deathYear: deathYear || '',
    location,
    country,
    eventType,
    eventMonth,
    eventYear,
    advancedSearch,
    selectedSources: CollectionTypeOptions.reduce((acc, option) => {
      acc[option.value] = advancedSearch ? source.includes(option.value) : true;
      return acc;
    }, {} as Record<string, boolean>),
  };

  const {
    fName: parsedFullName,
    mName: parsedMiddleName,
    lName: parsedLastName,
  } = parseFullName(fullName);

  if (parsedFullName !== '' && fName === '') {
    info.fName = parsedFullName;
  }
  if (parsedMiddleName !== '' && mName === '') {
    info.mName = parsedMiddleName;
  }
  if (parsedLastName !== '' && lName === '') {
    info.lName = parsedLastName;
  }

  // Age can only be present if it comes from Basic search form or any other form that contains
  // an age field. Otherwise, it will be an empty string since it comes from a refine results process
  // (which doesnt have an age field).
  // So if we have an age field, we use it to calculate the birth year and prepopulate the ConsolidatedFilterForm form
  if (age !== '') {
    const currentYear = new Date().getFullYear();
    info.birthYear = (currentYear - Number.parseInt(age)).toString();
    info.birthMonth = '';
  }

  return info;
}
/**
 * Calcula la duración en milisegundos, segundos y minutos entre dos timestamps.
 * @param {number} start - Timestamp inicial (ms)
 * @param {number} end - Timestamp final (ms)
 * @returns {{ ms: number, seconds: number, minutes: number }}
 */
export function getDurationBetweenTimestamps(start: number, end: number) {
  const ms = end - start;
  const seconds = ms / 1000;
  const minutes = seconds / 60;
  return { ms, seconds, minutes };
}

export function buildHeaderText(
  fullName: string,
  searchData?: AdvancedSearchURLParams
): string {
  if (!searchData) return fullName;

  const parts: string[] = [];

  const location = [searchData.city, searchData.state]
    .filter(Boolean)
    .join(', ');

  if (location) parts.push(`in ${location}`);

  if (searchData.age) parts.push(`age ${searchData.age}`);

  if (searchData.relatives?.length) {
    const names = searchData.relatives.map(
      (r) => `${r.firstName} ${r.lastName}`
    );

    const formattedNames =
      names.length === 1
        ? names[0]
        : `${names.slice(0, -1).join(', ')} and ${names.slice(-1)}`;

    parts.push(`associated to ${formattedNames}`);
  }

  return [fullName, ...parts].join(' ');
}
